import { useAppInsights } from 'vue3-application-insights'

/** Normalizes an unknown thrown value into an Error instance. */
export function toError(error: unknown): Error {
    if (error instanceof Error)
        return error

    if (typeof error === 'string')
        return new Error(error)

    if (error != null && typeof error === 'object' && 'message' in error)
        return new Error(String((error as any).message))

    try {
        return new Error(JSON.stringify(error))
    }
    catch {
        return new Error(`[non-serializable ${typeof error}]`)
    }
}

export function useTelemetry() {
    const appInsights = useAppInsights()

    function setTelemetryContext(context: TelemetryContext) {
        if (context.appName) {
            // should only set this up once
            appInsights.addTelemetryInitializer(item => {
                item.tags ??= {}
                item.tags["ai.cloud.role"] = context.appName
            })
        }
        if (context.appVersion) {
            appInsights.context.application.ver = context.appVersion
        }
        if (context.appSessionId) {
            appInsights.context.session.id = context.appSessionId
        }
        if (context.userId) {
            appInsights.context.user.id = context.userId
        }
    }
    
    function trackPageView(pageName: string, properties?: CustomData) {
        console.log(`TrackPageView.${pageName}`, properties)
        appInsights.trackPageView({ name: pageName, properties })
    }
    
    function trackEvent(eventName: string, properties?: CustomData) {
        console.log(`TrackEvent.${eventName}`, properties)
        appInsights.trackEvent({ name: eventName, properties })
    }
    
    function trackException(error: unknown, properties?: CustomData) {
        const exception = toError(error)
        console.error('TrackException', exception, properties)
        appInsights.trackException({ exception, properties })
    }
    
    return {
        setTelemetryContext,
        trackPageView,
        trackEvent,
        trackException,
    } as Telemetry
}

export interface Telemetry {
    setTelemetryContext: (context: TelemetryContext) => void,
    trackPageView: (pageName: string, properties?: CustomData) => void,
    trackEvent: (eventName: string, properties?: CustomData) => void,
    trackException: (error: unknown, properties?: CustomData) => void,    
}

export interface CustomData {
    [key: string]: any    
}

export interface TelemetryContext {
    appName?: string,
    appVersion?: string,
    appPackageVersion?: string,
    appSessionId?: string,
    userId?: string,
}
